// Producer / supervisor take review hooks.
//
// `takes.ts` in this folder is owned by the actor surface (upload + history).
// This module is the review queue: list pending-supervisor takes, approve,
// pickup, archive. All decisions go through the supervisor decision record
// server-side; the producer surface re-uses the same endpoint with their own
// org-scoped scope.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ListEnvelope, api, unwrapItems } from '../api'
import type { TakeRow } from './producer-types'

export const reviewQueueKey = (projectId: string) => ['review-queue', projectId] as const
export const reviewTakeKey = (takeId: string) => ['review-take', takeId] as const

/**
 * Server returns a slim review-item envelope (`take_id`, `cue_id`,
 * `character_id`, `actor_id`, `qc_overall`, `supervisor_decision`,
 * `recorded_at`). The producer dashboard renders a lightweight stream so
 * we map straight onto a `Partial<TakeRow>`-compatible shape — full take
 * detail is fetched on click via `useReviewTake`.
 */
export interface ReviewQueueItem {
  id: string
  cue_id: string
  character_id: string
  character_code: string
  cue_number: number | null
  line_text: string | null
  recorded_at: string
}

interface ReviewQueueRaw {
  take_id: string
  cue_id: string
  character_id: string
  actor_id: string
  qc_overall: 'pass' | 'warn' | 'fail'
  supervisor_decision: 'pending' | 'approved' | 'rejected_pickup' | 'rejected_archive'
  recorded_at: string
}

/** Pending-supervisor takes for one project. Realtime channel triggers refetch. */
export function useReviewQueue(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? reviewQueueKey(projectId) : ['review-queue', '__none__'],
    enabled: Boolean(projectId),
    queryFn: () =>
      api<ListEnvelope<ReviewQueueRaw>>(`/api/v1/projects/${projectId}/review-queue`)
        .then(unwrapItems)
        .then((rows): ReviewQueueItem[] =>
          rows.map((r) => ({
            id: r.take_id,
            cue_id: r.cue_id,
            character_id: r.character_id,
            character_code: r.character_id.slice(0, 8),
            cue_number: null,
            line_text: null,
            recorded_at: r.recorded_at,
          })),
        ),
  })
}

/** Single take detail with playback URL + waveform peaks. */
export function useReviewTake(takeId: string | undefined) {
  return useQuery({
    queryKey: takeId ? reviewTakeKey(takeId) : ['review-take', '__none__'],
    enabled: Boolean(takeId),
    queryFn: () => api<TakeRow>(`/api/v1/takes/${takeId}`),
  })
}

/**
 * Canonical decision enum — matches the BE `supervisor_decision` column
 * (see apps/api/src/routes/v1/supervisor.ts). The BE used to accept legacy
 * verb-form aliases ('approve' / 'pickup' / 'archive') as a temporary FE↔BE
 * compatibility shim; that has been removed. Use the canonical past-tense
 * names everywhere.
 */
export type ReviewDecision = 'approved' | 'rejected_pickup' | 'archived'

export interface DecideTakeInput {
  take_id: string
  decision: ReviewDecision
  /** Optional supervisor note. The server maps `notes` → `supervisor_note`. */
  supervisor_note?: string | null
}

export function useDecideTake(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ take_id, ...body }: DecideTakeInput) =>
      api<TakeRow>(`/api/v1/takes/${take_id}/decide`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: reviewQueueKey(projectId) })
      void qc.invalidateQueries({ queryKey: reviewTakeKey(vars.take_id) })
    },
  })
}

/** Bulk approve. Server validates each take; partial-success returns are OK. */
export function useBulkApproveTakes(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { take_ids: string[] }) =>
      api<{ approved: number; rejected: number }>(
        `/api/v1/projects/${projectId}/takes/bulk-approve`,
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reviewQueueKey(projectId) })
    },
  })
}
